import { useEffect, useState } from "react";
import toast from "../../utils/toast.js";
import {
  getMyCars,
  deleteCar,
  getCarStatus,
  toggleRouteStatus,
  deleteRoute,
} from "../../services/carsService.js";
import AddCarWizard from "../components/AddCarWizard.jsx";
import EditCarDetailsModal from "../components/EditCarDetailsModal.jsx";
import AddRouteModal from "../components/AddRouteModal.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";

const PAGE_SIZE = 10;

function stepForResume(resumeStep) {
  if (resumeStep === 2) return 1;
  if (resumeStep === 3) return 2;
  if (resumeStep === 4) return 3;
  return 1;
}

export default function MyCar() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [showWizard, setShowWizard] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [resumeAtStep, setResumeAtStep] = useState(1);

  const [detailsCar, setDetailsCar] = useState(null);
  const [routeModalCar, setRouteModalCar] = useState(null);
  const [routeModalRoute, setRouteModalRoute] = useState(null);

  const [carToDelete, setCarToDelete] = useState(null);
  const [routeToDelete, setRouteToDelete] = useState(null);

  function openAddWizard() {
    setEditingCar(null);
    setResumeAtStep(1);
    setShowWizard(true);
  }

  // Draft/pending cars still resume through the onboarding wizard.
  async function openResumeWizard(car) {
    try {
      const res = await getCarStatus(car._id);
      if (!res.success) {
        toast.error(res.message);
        return;
      }
      setEditingCar(res.data.car);
      setResumeAtStep(stepForResume(res.data.resumeStep));
      setShowWizard(true);
    } catch (err) {
      toast.error(err.response?.data?.message);
    }
  }

  function openAddRoute(car) {
    setRouteModalCar(car);
    setRouteModalRoute(null);
  }

  function openEditRoute(car, route) {
    setRouteModalCar(car);
    setRouteModalRoute(route);
  }

  async function handleToggleRoute(carId, routeId) {
    setCars((prev) =>
      prev.map((c) =>
        c._id !== carId
          ? c
          : {
              ...c,
              routes: (c.routes || []).map((r) =>
                r._id === routeId ? { ...r, isActive: !r.isActive } : r,
              ),
            },
      ),
    );
    try {
      const res = await toggleRouteStatus(routeId);
      if (!res.success) throw new Error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Couldn't toggle route.");
      // revert the optimistic flip
      setCars((prev) =>
        prev.map((c) =>
          c._id !== carId
            ? c
            : {
                ...c,
                routes: (c.routes || []).map((r) =>
                  r._id === routeId ? { ...r, isActive: !r.isActive } : r,
                ),
              },
        ),
      );
    }
  }

  async function confirmDeleteCar() {
    const car = carToDelete;
    setCarToDelete(null);
    try {
      const res = await deleteCar(car._id);
      if (!res.success) {
        toast.error(res.message);
        return;
      }
      toast.success(res.message);
      fetchCars();
    } catch (err) {
      toast.error(err.response?.data?.message);
    }
  }

  async function confirmDeleteRoute() {
    const route = routeToDelete;
    setRouteToDelete(null);
    try {
      const res = await deleteRoute(route._id);
      if (!res.success) {
        toast.error(res.message);
        return;
      }
      toast.success(res.message || "Route deleted.");
      fetchCars();
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't delete the route.");
    }
  }

  async function fetchCars() {
    try {
      const res = await getMyCars();
      if (res.success) {
        setCars(res.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't load your cars.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchCars();
  }, []);

  const totalPages = Math.max(1, Math.ceil(cars.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageCars = cars.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <main className="dash-content">
      <section className="dash-section active" data-section="cars">
        <div className="section-head-row">
          <h2>My Cars</h2>
          <button type="button" className="btn btn-outline btn-sm" onClick={openAddWizard}>
            + Add Car
          </button>
        </div>

        {loading ? (
          <div className="cars-table-card">
            <div className="cars-table-empty">Loading your cars...</div>
          </div>
        ) : cars.length === 0 ? (
          <div className="cars-table-card">
            <div className="cars-table-empty">
              No cars yet. Add your first car to start receiving bookings.
            </div>
          </div>
        ) : (
          <>
            <div className="cars-grid">
              {pageCars.map((car) => (
                <div className="car-card" key={car._id}>
                  <div className="car-card-top">
                    <div>
                      <h3 className="car-card-model">{car.carModel}</h3>
                      <span className="car-card-reg">
                        {car.registrationNo} · {car.carType} · {car.fuelType}
                      </span>
                    </div>
                    {car.isPublished ? (
                      <span className="status-badge published">Published</span>
                    ) : (
                      <span
                        className="status-badge pending"
                        onClick={() => openResumeWizard(car)}
                        style={{ cursor: "pointer" }}
                        title="Resume where you left off"
                      >
                        Pending
                      </span>
                    )}
                  </div>

                  <div className="car-card-bottom-row">
                    <div className="car-card-meta">
                      <span>👤 {car.driverName}</span>
                      <span>📱 {car.driverMobile}</span>
                    </div>
                    <div className="car-card-actions">
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() =>
                          car.isPublished ? setDetailsCar(car) : openResumeWizard(car)
                        }
                      >
                        {car.isPublished ? "Edit details" : "Resume"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm cars-table-delete"
                        onClick={() => setCarToDelete(car)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {car.isPublished && (
                    <>
                      <p className="car-card-price" style={{ marginTop: 16 }}>
                        <strong>ROUTES ON THIS CAR</strong>
                      </p>
                      <div className="routes-list">
                        {(car.routes || []).length === 0 && (
                          <p className="wizard-hint">No routes yet.</p>
                        )}
                        {(car.routes || []).map((route) => (
                          <div className="route-item" key={route._id}>
                            <div className="route-item-top">
                              <span className="route-item-path">
                                {route.fromCity} → {route.toCity}
                              </span>
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={route.isActive}
                                  onChange={() => handleToggleRoute(car._id, route._id)}
                                />
                                <span className="toggle-switch-track" />
                              </label>
                            </div>
                            <div className="route-item-bottom">
                              <div className="route-item-stats">
                                <span>
                                  {(route.dailyDepartures || []).join(", ") ||
                                    `${(route.specialTrips || []).length} special trip(s)`}
                                </span>
                                {route.allowPerSeatSharing && route.rateCard?.front > 0 && (
                                  <span>₹{route.rateCard.front} front</span>
                                )}
                                {route.allowPerSeatSharing && route.rateCard?.middle > 0 && (
                                  <span>₹{route.rateCard.middle} middle</span>
                                )}
                                {route.allowPerSeatSharing && route.rateCard?.rear > 0 && (
                                  <span>₹{route.rateCard.rear} rear</span>
                                )}
                                {route.allowFullCabBooking && route.rateCard?.fullCab > 0 && (
                                  <span>₹{route.rateCard.fullCab} full cab</span>
                                )}
                                <span>{route.isActive ? "Active" : "Paused"}</span>
                              </div>
                              <div className="cars-table-actions">
                                <button
                                  type="button"
                                  className="btn btn-outline btn-sm"
                                  onClick={() => openEditRoute(car, route)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline btn-sm cars-table-delete"
                                  onClick={() => setRouteToDelete(route)}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        style={{ marginTop: 14 }}
                        onClick={() => openAddRoute(car)}
                      >
                        + Add another route
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ← Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    type="button"
                    key={n}
                    className={`pagination-btn ${n === currentPage ? "active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <AddCarWizard
        isOpen={showWizard}
        car={editingCar}
        initialStep={resumeAtStep}
        onClose={() => {
          setShowWizard(false);
          setEditingCar(null);
          fetchCars();
        }}
      />

      <EditCarDetailsModal
        isOpen={!!detailsCar}
        car={detailsCar}
        onClose={() => setDetailsCar(null)}
        onSaved={fetchCars}
      />

      <AddRouteModal
        isOpen={!!routeModalCar}
        car={routeModalCar}
        route={routeModalRoute}
        onClose={() => {
          setRouteModalCar(null);
          setRouteModalRoute(null);
        }}
        onSaved={fetchCars}
      />

      <ConfirmDialog
        isOpen={!!carToDelete}
        title="Remove this car?"
        message={
          carToDelete &&
          `Remove ${carToDelete.carModel} (${carToDelete.registrationNo}) from your fleet? This can't be undone.`
        }
        confirmLabel="Yes, remove"
        cancelLabel="No, keep it"
        onConfirm={confirmDeleteCar}
        onCancel={() => setCarToDelete(null)}
      />

      <ConfirmDialog
        isOpen={!!routeToDelete}
        title="Remove this route?"
        message={
          routeToDelete && `Remove the ${routeToDelete.fromCity} → ${routeToDelete.toCity} route? This can't be undone.`
        }
        confirmLabel="Yes, remove"
        cancelLabel="No, keep it"
        onConfirm={confirmDeleteRoute}
        onCancel={() => setRouteToDelete(null)}
      />
    </main>
  );
}
